import { Resolve } from '@angular/router';
import { ImproverConstants } from '@improver/app-config';
import { ImproverRoutes } from '@improver/core';
import { InstanceSettingsResolver } from '@improver/fields/fields-data-layer/settings';
import { AuthGuard, InstanceGuard } from '@improver/infrastructure/auth-data-layer';
import { ImproverLayoutComponent } from '@improver/infrastructure/layout-shared';
import { ManagedEmploymentsAccessResolver, MyEmploymentsAccessResolver } from '@improver/people/employments-access-shared';
import { MobileGuard } from '@improver/shared/ui/features/responsive';
import { themeResolver } from './core/resolvers/instance-theme.resolver';

const featureResolvers: { [key: string]: new (...args: any[]) => Resolve<any> } = {
  instanceSettings: InstanceSettingsResolver,
  managedEmployments: ManagedEmploymentsAccessResolver,
  myManagedEmployments: MyEmploymentsAccessResolver,
};

export const AppRoutes: ImproverRoutes = [
  {
    path: '',
    component: ImproverLayoutComponent,
    resolve: { theme: themeResolver },
    children: [
      {
        path: ImproverConstants.ROUTES.INVITATIONS,
        data: {
          navbarActionsVisible: false,
          sidebarVisible: false,
        },
        loadChildren: () => import('@improver/people/invitation-feature').then(({ InvitationsModule }) => InvitationsModule),
      },
      {
        path: ImproverConstants.ROUTES.PROFILE,
        canLoad: [AuthGuard],
        loadChildren: () => import('@improver/people/profile-feature').then(({ ProfileRoutes }) => ProfileRoutes),
      },
      {
        path: ImproverConstants.ROUTES.MOBILE_DENIED,
        canLoad: [InstanceGuard, MobileGuard],
        data: {
          waitForPermissions: true,
        },
        resolve: featureResolvers,
        loadChildren: () =>
          import('./improver/denied/mobile-denied/mobile-denied.routes').then(({ mobileDeniedRoutes }) => mobileDeniedRoutes),
      },
      {
        path: ImproverConstants.ROUTES.PERSONNEL_MANAGEMENT,
        canActivate: [AuthGuard],
        canActivateChild: [InstanceGuard, MobileGuard],
        data: {
          waitForPermissions: true,
        },
        resolve: featureResolvers,
        loadChildren: () => import('@improver/people/personnel-feature').then(({ PersonnelRoutes }) => PersonnelRoutes),
      },
      {
        path: ImproverConstants.ROUTES.DEFAULT,
        canLoad: [AuthGuard, InstanceGuard],
        data: {
          waitForPermissions: true,
        },
        resolve: featureResolvers,
        loadChildren: () => import('@improver/dashboard/dashboard-feature').then(({ DashboardRoutes }) => DashboardRoutes),
      },
      {
        path: ImproverConstants.ROUTES.FOLLOW_UP,
        canLoad: [AuthGuard, InstanceGuard],
        canActivateChild: [MobileGuard],
        data: {
          waitForPermissions: true,
        },
        resolve: featureResolvers,
        loadChildren: () => import('./views/follow-up-view.routes').then(({ FollowUpViewRoutes }) => FollowUpViewRoutes),
      },
      {
        path: ImproverConstants.ROUTES.TIME,
        canLoad: [AuthGuard, InstanceGuard],
        canActivateChild: [InstanceGuard, MobileGuard],
        data: {
          waitForPermissions: true,
        },
        resolve: featureResolvers,
        loadChildren: () =>
          import('./views/time-and-attendance-view.routes').then(({ TimeAndAttendanceViewRoutes }) => TimeAndAttendanceViewRoutes),
      },
      {
        path: ImproverConstants.ROUTES.LEARNING,
        canLoad: [AuthGuard, InstanceGuard],
        canActivateChild: [MobileGuard],
        data: {
          waitForPermissions: true,
        },
        resolve: featureResolvers,
        loadChildren: () => import('@improver/learning/learning-feature').then(({ LmsModule }) => LmsModule),
      },
      {
        path: ImproverConstants.ROUTES.PROJECT_DETAILS,
        loadChildren: () => import('@improver/projects/projects-details-feature').then(({ ProjectDetailsModule }) => ProjectDetailsModule),
      },
      {
        path: ImproverConstants.ROUTES.LIBRARY,
        loadChildren: () => import('./views/library-view.routes').then(({ LibraryViewRoutes }) => LibraryViewRoutes),
      },
      {
        path: ImproverConstants.ROUTES.COMPETENCE,
        resolve: featureResolvers,
        loadChildren: () => import('./views/competence-view.routes').then(({ CompetenceViewRoutes }) => CompetenceViewRoutes),
      },
      {
        canLoad: [AuthGuard, InstanceGuard],
        canActivateChild: [MobileGuard],
        path: `${ImproverConstants.ROUTES.REPORTS}`,
        data: {
          waitForPermissions: true,
        },
        resolve: featureResolvers,
        loadChildren: () => import('@improver/reports/reports-feature').then(({ BuildInReportsRoutes }) => BuildInReportsRoutes),
      },
      {
        path: ImproverConstants.ROUTES.SETTINGS,
        canLoad: [AuthGuard, InstanceGuard],
        data: {
          waitForPermissions: true,
        },
        resolve: featureResolvers,
        loadChildren: () => import('@improver/settings/settings-view-feature').then(({ SettingsRoutes }) => SettingsRoutes),
      },
      {
        path: '',
        canLoad: [AuthGuard, InstanceGuard],
        redirectTo: ImproverConstants.ROUTES.DEFAULT,
        pathMatch: 'full',
      },
      {
        path: ImproverConstants.ROUTES.LIVE_FEED,
        redirectTo: ImproverConstants.ROUTES.DEFAULT,
        pathMatch: 'prefix',
      },
      {
        path: ImproverConstants.ROUTES.CREATE_POST,
        redirectTo: ImproverConstants.ROUTES.DEFAULT,
        pathMatch: 'prefix',
      },
      // Redirect for old path consistency
      {
        path: ImproverConstants.ROUTES.PROJECTS_VIEW,
        redirectTo: `${ImproverConstants.ROUTES.COMPETENCE}/${ImproverConstants.ROUTES.PROJECTS_VIEW}`,
        pathMatch: 'prefix',
      },
      {
        path: ImproverConstants.ROUTES.COMPETENCE_VIEW,
        redirectTo: `${ImproverConstants.ROUTES.COMPETENCE}/${ImproverConstants.ROUTES.COMPETENCE_VIEW}`,
        pathMatch: 'prefix',
      },
      {
        path: ImproverConstants.ROUTES.HANDBOOK,
        redirectTo: `${ImproverConstants.ROUTES.LIBRARY}/${ImproverConstants.ROUTES.HANDBOOK}`,
        pathMatch: 'prefix',
      },
    ],
  },
  {
    path: ImproverConstants.ROUTES.TWO_FA,
    loadChildren: () => import('./improver/auth/2fa/2fa.routes').then(({ TwoFaRoutes }) => TwoFaRoutes),
  },
  {
    path: ImproverConstants.ROUTES.ACCESS,
    loadChildren: () => import('./improver/no-access-layout/no-access-layout.routes').then(({ NoAccessRoutes }) => NoAccessRoutes),
  },
  {
    path: ImproverConstants.ROUTES.MAINTENANCE,
    loadChildren: () => import('./improver/maintenance/maintenance.routes').then(({ MaintenanceRoutes }) => MaintenanceRoutes),
  },
  {
    path: ImproverConstants.ROUTES.CHECKOUT,
    loadChildren: () => import('@improver/product/checkout-feature').then(({ CheckoutRoutes }) => CheckoutRoutes),
  },
  {
    path: ImproverConstants.ROUTES.INVITATION_EXPIRED,
    loadChildren: () =>
      import('./improver/auth/invitation-expired/invitation-expired.routes').then(({ InvitationExpiredRoutes }) => InvitationExpiredRoutes),
  },
  { path: ImproverConstants.ROUTES.AUTH, loadChildren: () => import('./improver/auth/auth.routes').then(({ AuthRoutes }) => AuthRoutes) },
  { path: '', loadChildren: () => import('./improver/login/login.routes').then(({ LoginRoutes }) => LoginRoutes) },
  {
    path: '**',
    redirectTo: ImproverConstants.ROUTES.DEFAULT,
    pathMatch: 'full',
  },
];
